import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAppSelector } from "../../../../common/hooks/hooks";
import { useToggleWishlistItemMutation } from "../api/wishlistApi";
import { selectIsInWishlist } from "../wishlistSelectors";

interface UseToggleWishlistItemProps {
  errorMessageOverride?: string;
}

/**
 * A custom hook to encapsulate all logic for toggling a product in the wishlist,
 * including dynamic success/info feedback and standardized error handling.
 *
 * @param productId The ID of the product to manage.
 * @param props Optional configuration for the hook.
 */
export const useToggleWishlistItem = (
  productId: number,
  props: UseToggleWishlistItemProps = {}
) => {
  const { t } = useTranslation();
  const { errorMessageOverride } = props;

  const isInWishlist = useAppSelector(selectIsInWishlist(productId));
  const [toggleWishlistItem, { isLoading }] = useToggleWishlistItemMutation();

  const handleToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoading) return;

    console.log(
      `[useToggleWishlistItem] Toggling Product ID: ${productId}. Current State: ${
        isInWishlist ? "In List" : "Not In List"
      }`
    );

    // Capture the state *before* the mutation to determine the correct message
    const wasInWishlist = isInWishlist;

    try {
      // Trigger the mutation
      const response = await toggleWishlistItem(productId).unwrap();
      console.log(
        "[useToggleWishlistItem] Mutation successful. Server response:",
        response
      );

      // Determine the correct success/info message
      const message = wasInWishlist
        ? t("feedback.wishlist.removedSuccess", "Removed from wishlist")
        : t("feedback.wishlist.addedSuccess", "Added to wishlist!");

      // Show a different style of toast for removal vs. addition
      if (wasInWishlist) {
        // 'info' style toast for removal
        toast(message, {
          icon: "💔",
          className: "app-toast app-toast--info",
        });
      } else {
        // Standard success toast for addition
        toast.success(message);
      }
    } catch (err) {
      console.error("[useToggleWishlistItem] Mutation failed:", err);
      // Handle errors
      const errorMessage =
        errorMessageOverride ||
        t("feedback.wishlist.toggleError", "Could not update wishlist.");
      toast.error(errorMessage);
    }
  };

  return {
    handleToggle,
    isInWishlist,
    isLoading,
  };
};
